// Market price table: every settled sale is kept for 30 days under its kind
// and key (species for creatures, item id, relic stat, shrine tier), so
// sellers can price near what buyers actually paid. The client asks for one
// key at a time ({ t: 'prices', kind, key }); nothing rides on hello.
import { SPECIES } from './species.ts';
import { isHeld } from './held.ts';
import { ITEM, RELIC_STATS } from './items.ts';
import { SHRINE_TIERS } from './shrines.ts';

export const PRICE_WINDOW_DAYS = 30;
export const PRICE_AVG_DAYS = 7;
/** Most sale rows sent per query (newest first); the stats cover the whole window. */
export const PRICE_ROWS_MAX = 200;
/** Days in the sparkline (one unit average per day, oldest first). */
export const PRICE_SPARK_DAYS = 30;

export type PriceKind = 'creature' | 'item' | 'relic' | 'shrine' | 'held';
export const PRICE_KINDS: readonly PriceKind[] = ['creature', 'item', 'relic', 'shrine', 'held'];

/** One sale. price is what the buyer paid for the whole lot of qty. */
export interface SaleRow { ts: number; qty: number; price: number; score?: number | null; level?: number | null; aura?: number | null; bloodline?: number | null; quality?: number | null }

export interface PriceStats {
  /** Sales in the window, and the units they moved. */
  sales: number;
  units: number;
  /** Unit price of the newest sale. */
  last: number | null;
  /** Units sold in the last 7 days and their average unit price (total paid / units). */
  units7: number;
  avg7: number | null;
  /** Lowest and highest unit price in the window. */
  min: number | null;
  max: number | null;
}

export interface PriceTableDTO {
  kind: PriceKind;
  key: string;
  /** Gem prices (shared/gems.ts); absent means coins. */
  cur?: 'gems';
  stats: PriceStats;
  /** Lowest unit price among open listings now, or null. */
  lowest: number | null;
  /** Newest first, at most PRICE_ROWS_MAX. */
  rows: SaleRow[];
  /** Average unit price per day, oldest first; null on days without a sale. */
  spark: (number | null)[];
  now: number;
}

/** The key a sale is filed under. */
export function saleKey(kind: string, ref: string, snapshot: unknown): string {
  if (kind === 'creature') return String((snapshot as { species?: string } | null)?.species ?? '');
  if (kind === 'item') return ref;
  if (kind === 'relic') return String((snapshot as { stat?: string } | null)?.stat ?? '');
  if (kind === 'shrine') return String((snapshot as { tier?: number } | null)?.tier ?? '');
  if (kind === 'held') return String((snapshot as { item?: string } | null)?.item ?? '');
  return '';
}

/** A query the server answers: a known kind and a key that exists and can be traded. */
export function validPriceQuery(kind: unknown, key: unknown): kind is PriceKind {
  if (typeof kind !== 'string' || typeof key !== 'string' || key.length > 40) return false;
  if (kind === 'creature') return Object.hasOwn(SPECIES, key);
  if (kind === 'item') return Object.hasOwn(ITEM, key) && !!ITEM[key].tradable;
  if (kind === 'relic') return RELIC_STATS.some((r) => r.stat === key);
  if (kind === 'shrine') return /^\d$/.test(key) && Number(key) < SHRINE_TIERS.length;
  if (kind === 'held') return isHeld(key);
  return false;
}

const unit = (r: SaleRow) => r.price / Math.max(1, r.qty);

/** Stats of rows already limited to the window (any order). */
export function priceStats(rows: readonly SaleRow[], now: number): PriceStats {
  const since7 = now - PRICE_AVG_DAYS * 86_400_000;
  let units = 0, units7 = 0, paid7 = 0, min = Infinity, max = -Infinity, newest: SaleRow | null = null;
  for (const r of rows) {
    const q = Math.max(1, r.qty), u = unit(r);
    units += q;
    if (r.ts >= since7) { units7 += q; paid7 += r.price; }
    if (u < min) min = u;
    if (u > max) max = u;
    if (!newest || r.ts > newest.ts) newest = r;
  }
  return {
    sales: rows.length, units,
    last: newest ? unit(newest) : null,
    units7, avg7: units7 ? paid7 / units7 : null,
    min: rows.length ? min : null, max: rows.length ? max : null,
  };
}

/** Average unit price per day over the last `days` days, oldest first. */
export function priceSpark(rows: readonly SaleRow[], now: number, days = PRICE_SPARK_DAYS): (number | null)[] {
  const paid = new Array<number>(days).fill(0), units = new Array<number>(days).fill(0);
  for (const r of rows) {
    const ago = Math.floor((now - r.ts) / 86_400_000);
    if (ago < 0 || ago >= days) continue;
    paid[days - 1 - ago] += r.price; units[days - 1 - ago] += Math.max(1, r.qty);
  }
  return paid.map((p, i) => (units[i] ? p / units[i] : null));
}
